import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Phone, TrendingUp, Zap, Building2, SkipForward } from "lucide-react";

const DISCOVERY_FIELDS = [
  ["known_renewal_date", "Commercial Renewal Date", "date"], ["current_broker", "Current Broker", "text"],
  ["current_carrier", "Current Carrier", "text"], ["benefits_offered", "Employee Benefits Offered", "text"],
  ["retirement_plan", "Retirement Plan Offered", "text"], ["key_person_exposure", "Key-Person Exposure", "text"],
  ["succession_interest", "Succession Interest", "text"], ["exit_timeframe", "Business Sale / Exit Timeframe", "text"],
  ["financial_planning_interest", "Financial-Planning Interest", "text"], ["ownership_type", "Ownership Structure", "text"],
];

export default function CallWorkspace() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ws, setWs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contactId, setContactId] = useState("");
  const [notes, setNotes] = useState("");
  const [discovery, setDiscovery] = useState({});
  const [showDiscovery, setShowDiscovery] = useState(false);
  const [oppLine, setOppLine] = useState("");
  const [showOpp, setShowOpp] = useState(false);
  const [extra, setExtra] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/boi/workspace/${id}`);
      setWs(data);
      const prim = data.contacts.find((c) => c.is_primary) || data.contacts[0];
      setContactId(prim?.id || ""); setNotes(""); setDiscovery({}); setExtra({});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setWs(false); }
    setLoading(false);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const disposition = async (dkey, advance) => {
    const d = ws.dispositions.find((x) => x.key === dkey);
    const body = { disposition: dkey, contact_id: contactId, notes, discovery, ...extra };
    if (d?.require?.includes("callback_date") && !extra.callback_date) { toast.error("Callback date required."); return; }
    if (d?.require?.includes("appointment_date") && !extra.appointment_date) { toast.error("Appointment date required."); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/boi/relationships/${id}/disposition`, body);
      toast.success(`Saved: ${data.disposition}`);
      if (data.prompt_opportunity) setShowOpp(true);
      if (advance) {
        if (data.next_relationship_id) nav(`/call-workspace/${data.next_relationship_id}`);
        else { toast.info("Queue complete."); nav("/call-queue"); }
      } else { load(); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const createOpp = async () => {
    if (!oppLine) { toast.error("Select a business line."); return; }
    try {
      const { data } = await api.post(`/boi/relationships/${id}/create-opportunity`, { business_line_id: oppLine });
      toast.success(`Opportunity created${data.routes_to_insurance ? " (routed to insurance workflow)" : ""}.`);
      setShowOpp(false); setOppLine(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="p-8 text-mutedsea text-sm" data-testid="ws-loading">Loading workspace…</div>;
  if (!ws) return <div className="p-8" data-testid="ws-notfound"><p className="text-mutedsea text-sm mb-3">Not found or outside your book.</p><Button variant="outline" onClick={() => nav("/call-queue")}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button></div>;

  const r = ws.relationship;
  const contact = ws.contacts.find((c) => c.id === contactId);
  const bno = ws.best_next_opportunity, bna = ws.best_next_action;

  return (
    <div data-testid="call-workspace-page">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => nav("/call-queue")} className="text-sm text-atlantic hover:text-navy flex items-center gap-1 nav-link" data-testid="ws-back"><ArrowLeft className="w-4 h-4" />Queue</button>
        {r.do_not_contact && <Badge variant="outline" className="bg-seared/10 text-seared border-seared/30">Do Not Contact</Badge>}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Company + Contact */}
          <div className="bg-white border border-border rounded-sm p-4">
            <div className="flex items-center gap-2 mb-1"><Building2 className="w-5 h-5 text-atlantic" /><h1 className="font-serif text-2xl text-navy" data-testid="ws-company">{r.name}</h1></div>
            <div className="text-sm text-mutedsea">{[r.industry, r.address_state, r.revenue && `$${Number(r.revenue).toLocaleString()}`, r.employee_count && `${r.employee_count} emp`, r.locations && `${r.locations} loc`].filter(Boolean).join(" · ") || "—"}</div>
            <div className="mt-3">
              <div className="text-xs uppercase text-mutedsea tracking-wide mb-1">Who are you calling?</div>
              <select className="w-full h-9 border border-border rounded-sm px-2 text-sm" value={contactId} onChange={(e) => setContactId(e.target.value)} data-testid="ws-contact-select">
                <option value="">— select contact —</option>
                {ws.contacts.map((c) => <option key={c.id} value={c.id} disabled={c.do_not_contact}>{c.name} · {c.title || c.decision_role}{c.do_not_contact ? " (DNC)" : ""}</option>)}
              </select>
              {contact && <div className="mt-2 flex gap-3 text-sm" data-testid="ws-contact-details">
                {contact.phone && <a href={`tel:${contact.phone}`} className="text-atlantic hover:underline inline-flex items-center gap-1" data-testid="ws-tel-link"><Phone className="w-3.5 h-3.5" />{contact.phone}</a>}
                {contact.email && <span className="text-mutedsea">{contact.email}</span>}
              </div>}
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white border border-border rounded-sm p-4">
            <div className="text-xs uppercase text-mutedsea tracking-wide mb-1">Call Notes</div>
            <Textarea rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Document the conversation…" data-testid="ws-notes" />
            <button onClick={() => setShowDiscovery((s) => !s)} className="text-xs text-atlantic hover:underline mt-2" data-testid="ws-discovery-toggle">{showDiscovery ? "Hide" : "Add"} structured discovery</button>
            {showDiscovery && (
              <div className="grid grid-cols-2 gap-2 mt-2" data-testid="ws-discovery">
                {DISCOVERY_FIELDS.map(([k, l, t]) => (
                  <label key={k} className="block"><span className="text-xs text-mutedsea">{l}</span>
                    <Input type={t} className="h-8 mt-0.5" value={discovery[k] || ""} onChange={(e) => setDiscovery((d) => ({ ...d, [k]: e.target.value }))} data-testid={`discovery-${k}`} /></label>
                ))}
              </div>
            )}
          </div>

          {/* Extra fields for callback/appointment */}
          <div className="bg-white border border-border rounded-sm p-4 grid grid-cols-2 gap-3">
            <label className="block"><span className="text-xs text-mutedsea">Callback / Appointment Date</span><Input type="date" className="h-8 mt-0.5" value={extra.callback_date || extra.appointment_date || ""} onChange={(e) => setExtra((x) => ({ ...x, callback_date: e.target.value, appointment_date: e.target.value }))} data-testid="ws-schedule-date" /></label>
            <label className="block"><span className="text-xs text-mutedsea">Next Follow-Up (general)</span><Input type="date" className="h-8 mt-0.5" value={extra.next_action_date || ""} onChange={(e) => setExtra((x) => ({ ...x, next_action_date: e.target.value }))} data-testid="ws-followup-date" /></label>
          </div>

          {/* Dispositions */}
          <div className="bg-white border border-border rounded-sm p-4">
            <div className="text-xs uppercase text-mutedsea tracking-wide mb-2">Disposition</div>
            <div className="flex flex-wrap gap-1.5" data-testid="ws-dispositions">
              {ws.dispositions.map((d) => (
                <div key={d.key} className="flex">
                  <button onClick={() => disposition(d.key, false)} disabled={saving} data-testid={`disp-${d.key}`}
                    className="text-xs px-2.5 py-1.5 border border-border rounded-l-sm hover:bg-ice text-navy">{d.label}</button>
                  <button onClick={() => disposition(d.key, true)} disabled={saving} title="Save & Next" data-testid={`disp-next-${d.key}`}
                    className="px-1.5 py-1.5 border border-l-0 border-border rounded-r-sm hover:bg-navy hover:text-white bg-cloud"><SkipForward className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowOpp(true)} data-testid="ws-create-opp-btn">Create Opportunity</Button>
            </div>
          </div>
        </div>

        {/* Sidebar: scores + BNO/BNA + history */}
        <div className="space-y-4">
          <div className="bg-navy text-white rounded-sm p-4" data-testid="ws-bno">
            <div className="flex items-center gap-1.5 text-sky text-xs uppercase tracking-wide mb-1"><TrendingUp className="w-4 h-4" />Best Next Opportunity</div>
            {bno ? <div><span className="font-serif text-xl">{bno.business_line_name}</span> <span className="text-sky">{bno.score}</span></div> : <div className="text-sky/80 text-sm">—</div>}
            <div className="flex items-center gap-1.5 text-sky text-xs uppercase tracking-wide mt-3 mb-1"><Zap className="w-4 h-4" />Best Next Action</div>
            <div className="font-serif text-lg">{bna?.action || "—"}</div>
          </div>
          <div className="bg-white border border-border rounded-sm p-4">
            <div className="text-xs uppercase text-mutedsea tracking-wide mb-2">Opportunity Scores</div>
            {ws.scores.map((s) => (
              <div key={s.business_line_id} className="flex items-center justify-between text-sm py-1 border-b border-border last:border-0" data-testid={`ws-score-${s.business_line_id}`}>
                <span className="text-navy">{s.business_line_name}</span>
                <span><span className="font-serif text-navy">{s.score}</span> <span className="text-xs text-mutedsea">{s.data_confidence}%</span>{s.opp_status ? <Badge variant="outline" className="ml-1 text-xs bg-ice">{s.opp_status}</Badge> : null}</span>
              </div>
            ))}
            {ws.scores.length === 0 && <p className="text-xs text-mutedsea">No scores.</p>}
          </div>
          <div className="bg-white border border-border rounded-sm p-4">
            <div className="text-xs uppercase text-mutedsea tracking-wide mb-2">Recent History</div>
            {ws.chronology.slice(0, 6).map((a) => (
              <div key={a.id} className="text-xs border-l-2 border-sky/40 pl-2 mb-1.5" data-testid={`ws-chrono-${a.id}`}>
                <div className="text-navy">{a.subject}</div><div className="text-mutedsea">{(a.timestamp || "").slice(0, 16).replace("T", " ")}</div>
              </div>
            ))}
            {ws.chronology.length === 0 && <p className="text-xs text-mutedsea">No history.</p>}
          </div>
        </div>
      </div>

      <Dialog open={showOpp} onOpenChange={(o) => !o && setShowOpp(false)}>
        <DialogContent data-testid="ws-opp-dialog">
          <DialogHeader><DialogTitle className="font-serif text-navy">Create Opportunity</DialogTitle></DialogHeader>
          <p className="text-xs text-mutedsea">Reuses the existing opportunity architecture — one relationship can hold many opportunities. Commercial P&C routes into the insurance workflow.</p>
          <select className="w-full h-9 border border-border rounded-sm px-2 text-sm" value={oppLine} onChange={(e) => setOppLine(e.target.value)} data-testid="opp-line-select">
            <option value="">— select business line —</option>
            {ws.business_lines.concat((ws.scores || []).map((s) => ({ id: s.business_line_id, name: s.business_line_name }))).filter((v, i, a) => a.findIndex((x) => x.id === v.id) === i).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <DialogFooter><Button onClick={createOpp} className="bg-navy hover:bg-atlantic" data-testid="opp-create-submit">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
